import { urls } from '~/preloaded';
import settings from '~/settings';
import { BATTLE_TYPES, SEASON_TYPES } from '~/constants';
import { get, post } from '~/helpers/api';
import { getLastSeasonOfType } from '~/helpers/ladder';
import Loader from '~/helpers/loader';
import { t } from '~/helpers/localization';
import { snakeToCamel } from '~/helpers/strings';
import { getFirstAssignableRole, ROLE_NAMES } from '~/roles';
import { currentClanSelector } from '~/store/selectors/currentAccountSelector';
import { currentAccountActions, type DiffObjectType } from '~/store/slices/currentAccountSlice';
import { updateSupplyDataThunk } from '~/store/thunks/supplyThunks';

import {
  sendChangeCommanderErrorNotification,
  sendChangeRoleErrorNotification,
  sendChangeRoleNotification,
  sendCurrentAccountTransferredLeadershipNotification,
  sendNewCommanderNotification,
  sendRemoveMembersErrorNotification,
  sendRemoveMembersNotification,
} from '~/web2ClientAPI/notifications';

import { actionsClanRename } from './ActionClanRename';
import { actionsSupply } from './ActionSupply';

import type { Actions } from '~/Actions/index';
import type { InferActionsType } from '~/Reducers';
import type { IClanDisplay } from '~/Reducers/ReducerSupply';
import type { SOCIAL_NETWORKS } from '~/constants';
import type { AxiosCustomError } from '~/helpers/api';
import type { AppAsyncThunk } from '~/store';

import type {
  IApiChangeCommanderError,
  IApiChangeCommanderResponse,
  IApiChangeRoleError,
  IApiChangeRoleResponse,
  IApiClanMembersResponse,
  IApiClanResponse,
  IApiRemoveMembersError,
  IApiRemoveMembersResponse,
} from '~/types/api';
import type {
  ClanMemberRaw,
  IAchievement,
  IBattlesStats,
  IBattleType,
  IClanMember,
  IClanMemberWithRole,
  IClanProfileStatistics,
  PreModerationField,
} from '~/types/declaration';
import type { Nullable } from '~/types/generics';

// TODO: move to separate slices

// Clans
export const APPLICATIONS_UPDATE_CLAN_ACTIVE_APPLICATIONS = 'APPLICATIONS_UPDATE_CLAN_ACTIVE_APPLICATIONS';
export const CHANGE_CLAN_ATTRIBUTES_SUCCESS = 'CHANGE_CLAN_ATTRIBUTES_SUCCESS';
export const DECREASE_CLAN_MEMBERS = 'DECREASE_CLAN_MEMBERS';
export const DECREASE_CLAN_RESOURCE = 'DECREASE_CLAN_RESOURCE';
export const INCREASE_CLAN_MEMBERS = 'INCREASE_CLAN_MEMBERS';
export const INCREASE_MAX_CLAN_SIZE = 'INCREASE_MAX_CLAN_SIZE';
export const START_UPDATE_CLAN = 'START_UPDATE_CLAN';
export const UPDATE_ACHIEVEMENTS = 'UPDATE_ACHIEVEMENTS';
export const UPDATE_CLAN = 'UPDATE_CLAN';
export const UPDATE_CLAN_FROM_SYNC = 'UPDATE_CLAN_FROM_SYNC';
export const UPDATE_WOWS_LADDER = 'UPDATE_WOWS_LADDER';
// ClanMembers
export const CLANS_CHANGE_BATTLE_TYPE = 'CLANS_CHANGE_BATTLE_TYPE';
export const CLANS_CHANGE_TEAM_NUMBER = 'CLANS_CHANGE_TEAM_NUMBER';
export const SET_CLAN_PROFILE_CURRENT_SEASON = 'SET_CLAN_PROFILE_CURRENT_SEASON';
export const SET_CLAN_PROFILE_CURRENT_SEASON_TYPE = 'SET_CLAN_PROFILE_CURRENT_SEASON_TYPE';
export const START_UPDATE_MEMBERS = 'START_UPDATE_MEMBERS';
export const TOGGLE_MEMBERS_SORTING = 'TOGGLE_MEMBERS_SORTING';
export const UPDATE_MEMBERS = 'UPDATE_MEMBERS';
export const UPDATE_STATISTICS = 'UPDATE_STATISTICS';
// ClanMembersOperation
export const CLEAR_MEMBERS_OPERATION = 'CLEAR_MEMBERS_OPERATION';
export const SELECT_ROLE_MEMBERS_OPERATION = 'SELECT_ROLE_MEMBERS_OPERATION';
export const TOGGLE_ALL_MEMBERS_TICK = 'TOGGLE_ALL_MEMBERS_TICK';
export const TOGGLE_MEMBER_TICK = 'TOGGLE_MEMBER_TICK';
export const UNTICK_CLAN_MEMBERS = 'UNTICK_CLAN_MEMBERS';

type ClanAttributeType = {
  clanId: number;
  name?: string;
  tag?: string;
  rawDescription?: string;
  color?: string;
};

type ClanType = {
  id: number;
  rawDescription: string;
  createdAt: string;
  isDisbanded: boolean;
  accumulativeResource: number;
  personalResource: number;
  maxMembersCount: number;
};

type WowsLadderRatingsMaxPosition = {
  division: number;
  division_rating: number;
  league: number;
  public_rating: number;
};

export type RatingsStage = {
  id: number;
  type: string;
  victories_required?: number;
  battle_result_id?: number;
  target_public_rating?: number;
  progress?: string[];
  target?: string;
  battles?: number;
  target_division?: number;
  target_league?: number;
  target_division_rating?: number;
};

export type WowsLadderRatingTeam = {
  battles_count: number;
  current_winning_streak: number;
  division: number;
  division_rating: number;
  division_rating_max: number;
  id: number;
  initial_public_rating: number;
  is_best_season_rating: boolean;
  is_qualified: boolean;
  last_win_at: string;
  league: number;
  longest_winning_streak: number;
  max_position: WowsLadderRatingsMaxPosition;
  max_public_rating: number;
  public_rating: number;
  realm: Nullable<string>;
  season_number: number;
  stage: Nullable<RatingsStage>;
  status: string;
  team_number: 1 | 2;
  wins_count: number;
};

export type ClanStatistics = {
  battles_count: number;
  damage_per_battle: number;
  exp_per_battle: number;
  wins_percentage: number;
};

export type WowsLadderType = {
  battles_count: number;
  color: number;
  current_winning_streak: number;
  division: number;
  division_rating: number;
  division_rating_max: number;
  id: number;
  initial_public_rating: number;
  is_banned: boolean;
  is_best_season_rating: boolean;
  is_disbanded: boolean;
  is_qualified: boolean;
  last_battle_at: string;
  last_win_at: string | null;
  leading_team_number: number;
  league: number;
  longest_winning_streak: number;
  max_position: WowsLadderRatingsMaxPosition;
  max_public_rating: number;
  members_count: number;
  planned_prime_time: number | null;
  prime_time: string | null;
  public_rating: number;
  rating_realm: string | null;
  ratings: WowsLadderRatingTeam[];
  realm: string | null;
  season_number: number;
  stage: {
    type: BATTLE_TYPES.BRAWL_CVC;
    battles: null;
    id: null;
    victories_required: null;
    target: null;
    target_division_rating: null;
    battle_result_id: null;
    target_public_rating: null;
    target_division: null;
    target_league: null;
    progress: unknown[];
  } | null;
  status: string;
  team_number: number;
  total_battles_count: number;
  wins_count: number;
};

export type ClanInfoTypeRaw = {
  accumulative_resource: number;
  active_applications: Array<string | { id: number }>;
  active_invites: string[];
  color: string;
  community_urls: {
    [key in SOCIAL_NETWORKS]?: string;
  };
  created_at: string;
  description: string;
  id: number;
  is_application_sent: boolean;
  is_disbanded: boolean;
  is_invite_received: boolean;
  is_suited_for_autorecruiting: boolean;
  leveling: number;
  max_members_count: number;
  members_count: number;
  motto: string;
  name: string;
  personal_resource: number;
  pre_moderation: PreModerationField[];
  raw_description: string;
  recruiting_policy: string;
  recruiting_restrictions: {
    battles_count: number;
    win_rate: number;
  };
  stats: IBattlesStats;
  tag: string;
};

export type ClanInfoType = {
  accumulativeResource: number;
  active_applications: Array<string | { id: number }>;
  active_invites: string[];
  color: string;
  createdAt: string;
  description: string;
  id: number;
  isDisbanded: boolean;
  is_application_sent: boolean;
  is_invite_received: boolean;
  is_suited_for_autorecruiting: boolean;
  leveling: number;
  maxMembersCount: number;
  members_count: number;
  motto: string;
  name: string;
  personalResource: number;
  preModeration: PreModerationField[];
  rawDescription: string;
  recruiting_policy: string;
  recruiting_restrictions: {
    battles_count: number;
    win_rate: number;
  };
  stats: IBattlesStats;
  tag: string;
  communityUrls: {
    [key in SOCIAL_NETWORKS]?: string;
  };
};

export type ActionsType = InferActionsType<typeof actionsClanProfile>;

export const actionsClanProfile = {
  changeClanAttributesSuccessed: (data: ClanAttributeType) =>
    ({
      type: CHANGE_CLAN_ATTRIBUTES_SUCCESS,
      data,
    }) as const,

  setClanProfileCurrentSeason: (currentSeason: number) =>
    ({
      type: SET_CLAN_PROFILE_CURRENT_SEASON,
      currentSeason,
    }) as const,

  startUpdateClan: (clanId: number) =>
    ({
      type: START_UPDATE_CLAN,
      clanId,
    }) as const,

  updateClan: (clan: ClanType) =>
    ({
      type: UPDATE_CLAN,
      clan,
    }) as const,

  updateClanFromSync: (clanId: number, clanInfo: DiffObjectType) =>
    ({
      type: UPDATE_CLAN_FROM_SYNC,
      clanId,
      clanInfo,
    }) as const,

  increaseMaxClanSize: (clanId: number, extraMembers: number) =>
    ({
      type: INCREASE_MAX_CLAN_SIZE,
      clanId,
      extraMembers,
    }) as const,

  setClanError: (clanId: number, error: string) =>
    ({
      type: UPDATE_CLAN,
      clan: {
        id: clanId,
        error,
      },
    }) as const,

  decreaseClanMembersCount: (clanId: number, count: number) =>
    ({
      type: DECREASE_CLAN_MEMBERS,
      clanId,
      count,
    }) as const,

  increaseClanMembersCount: (clanId: number, count: number) =>
    ({
      type: INCREASE_CLAN_MEMBERS,
      clanId,
      count,
    }) as const,

  decreaseClanResource: (clanId: number, resource: number) =>
    ({
      type: DECREASE_CLAN_RESOURCE,
      clanId,
      resource,
    }) as const,

  startUpdateMembers: (clanId: number) =>
    ({
      type: START_UPDATE_MEMBERS,
      clanId,
    }) as const,

  toggleMemberTick: (memberId: number) =>
    ({
      type: TOGGLE_MEMBER_TICK,
      memberId,
    }) as const,

  toggleAllMembersTick: (memberIds: Array<number>) =>
    ({
      type: TOGGLE_ALL_MEMBERS_TICK,
      memberIds,
    }) as const,

  clearMembersOperation: () =>
    ({
      type: CLEAR_MEMBERS_OPERATION,
    }) as const,

  selectRoleMembersOperation: (roleName: ROLE_NAMES) =>
    ({
      type: SELECT_ROLE_MEMBERS_OPERATION,
      roleName,
    }) as const,

  untickClanMembers: (memberIds: Array<number>) =>
    ({
      type: UNTICK_CLAN_MEMBERS,
      memberIds,
    }) as const,

  updateWowsLadder: (clanId: number, wowsLadder: WowsLadderType) =>
    ({
      type: UPDATE_WOWS_LADDER,
      clanId,
      wowsLadder,
    }) as const,

  updateAchievements: (clanId: number, achievements: IAchievement[]) =>
    ({
      type: UPDATE_ACHIEVEMENTS,
      clanId,
      achievements,
    }) as const,

  changeClanBattleType: (clanId: number, battleType: IBattleType) =>
    ({
      type: CLANS_CHANGE_BATTLE_TYPE,
      clanId,
      battleType,
    }) as const,

  changeClanTeamNumber: (clanId: number, teamNumber: number) =>
    ({
      type: CLANS_CHANGE_TEAM_NUMBER,
      clanId,
      teamNumber,
    }) as const,

  updateClanActiveApplications: (clanId: number, applicationId: number) =>
    ({
      type: APPLICATIONS_UPDATE_CLAN_ACTIVE_APPLICATIONS,
      clanId,
      applicationId,
    }) as const,

  setClanProfileCurrentSeasonType: (currentSeasonType: SEASON_TYPES) =>
    ({
      type: SET_CLAN_PROFILE_CURRENT_SEASON_TYPE,
      currentSeasonType,
    }) as const,

  updateMembers: (clanId: number, members: IClanMember[]) =>
    ({
      type: UPDATE_MEMBERS,
      clanId,
      error: '',
      isFetching: false,
      members,
    }) as const,

  updateClanStatistics: (clanId: number, isHiddenStatistics: boolean, statistics: IClanProfileStatistics | null) =>
    ({
      type: UPDATE_STATISTICS,
      clanId,
      isHiddenStatistics,
      statistics,
    }) as const,

  setMembersError: (clanId: number, error: string) =>
    ({
      type: UPDATE_MEMBERS,
      clanId: clanId,
      error: error,
      isFetching: false,
      members: [] as IClanMember[],
    }) as const,

  toggleMembersSorting: (clanId: number, field: string, isAsc: boolean, hiddenSortingNames: string[]) =>
    ({
      type: TOGGLE_MEMBERS_SORTING,
      clanId,
      error: '',
      hiddenSortingNames,
      isFetching: false,
      sort: {
        field,
        isAsc,
      },
    }) as const,
};

const sendNotificationByClanOperationalError = (
  error: IApiChangeRoleError | IApiRemoveMembersError,
  clan: IClanDisplay,
  members: IClanMember[],
  sendErrorNotification: typeof sendChangeRoleErrorNotification,
) => {
  const additionalInfo = error?.additional_info;
  const reason = error?.title?.toLowerCase() ?? additionalInfo?.reason;

  if (reason === 'insufficient_permissions') {
    if (additionalInfo && 'role' in additionalInfo) {
      sendErrorNotification('insufficient_permissions');
    } else {
      sendErrorNotification('excluded_from_clan', clan);
    }
  } else if (reason === 'members_not_in_clan') {
    const missingMemberIds = additionalInfo && 'ids' in additionalInfo ? additionalInfo.ids : [];
    const missingMembers = members.filter((member) => {
      return missingMemberIds?.includes(member.id);
    });
    if (missingMembers.length > 0) {
      const missingMembersNames = missingMembers.map((member) => member.name);
      sendErrorNotification('members_not_in_clan', missingMembersNames, clan);
    } else {
      sendErrorNotification();
    }
  } else if (reason === 'role_rank_low') {
    const roles = additionalInfo && 'roles' in additionalInfo ? additionalInfo.roles : [];
    const newMembersInfo: IClanMember[] = [];
    members.forEach((member) => {
      if (roles?.[member.id]) {
        newMembersInfo.push({
          name: member.name,
          roleName: roles[member.id],
        } as IClanMember);
      }
    });
    sendErrorNotification('role_rank_low', newMembersInfo);
  } else {
    sendErrorNotification();
  }
};

export const removeMembers =
  (membersToRemoveIds: number[]): AppAsyncThunk =>
  async (dispatch, getState) => {
    const state = getState();
    const currentClan = currentClanSelector(state);
    const currentClanId = state.currentAccount.clanId;

    if (!currentClan || !currentClanId) {
      return;
    }

    const members = state.members.clans[currentClanId]?.members;
    const removedMembers = members.filter((member) => membersToRemoveIds.includes(member.id));

    try {
      const url = urls.removeMembers.replace('{clan_id}', String(currentClanId));
      const payload = { user_ids: membersToRemoveIds };

      const response = await post<IApiRemoveMembersResponse>(url, payload);

      if ('error' in response) {
        sendNotificationByClanOperationalError(
          response,
          currentClan,
          removedMembers,
          sendRemoveMembersErrorNotification,
        );
        throw response.error;
      }

      const names = removedMembers.map((member) => member.name);
      sendRemoveMembersNotification(names, currentClan);

      dispatch(actionsClanProfile.untickClanMembers(membersToRemoveIds));
      dispatch(actionsClanProfile.decreaseClanMembersCount(currentClanId, removedMembers.length));

      await dispatch(fetchMembers(currentClanId));
    } catch (error: unknown) {
      const axiosError = error as AxiosCustomError<IApiRemoveMembersError>;

      if (typeof error === 'object') {
        if (axiosError?.data) {
          sendNotificationByClanOperationalError(
            axiosError?.data,
            currentClan,
            removedMembers,
            sendRemoveMembersErrorNotification,
          );
          return;
        }
      }

      sendRemoveMembersErrorNotification();
    }
  };

export const changeRole =
  (memberToChangeId?: number): AppAsyncThunk =>
  async (dispatch, getState) => {
    const state = getState();
    const { clanId: currentClanId, roleName: currentRoleName } = state.currentAccount;
    const { selectedIds, selectedRole } = state.membersOperation;

    if (!currentRoleName || !currentClanId) {
      return;
    }

    const memberIds = memberToChangeId ? [memberToChangeId] : selectedIds;
    const roleName = selectedRole || getFirstAssignableRole(currentRoleName) || ROLE_NAMES.PRIVATE;

    const clan = state.clans.items[currentClanId];
    const members = state.members.clans[currentClanId].members;

    const membersUpdated = members.map((member) => {
      if (memberIds.includes(member.id)) {
        return Object.assign({}, member, { roleName });
      }
      return member;
    });
    const membersToChange = members.filter((member) => memberIds.includes(member.id));

    try {
      const url = urls.changeRole.replace('{clan_id}', String(currentClanId));
      const payload = { user_ids: memberIds, role: roleName };

      const response = await post<IApiChangeRoleResponse>(url, payload);

      if (response?.error && response?.additional_info?.reason !== 'role_already_assigned') {
        sendNotificationByClanOperationalError(response, clan, membersToChange, sendChangeRoleErrorNotification);
        throw response.error;
      }

      const names = membersToChange.map((member) => member.name);
      sendChangeRoleNotification({
        names,
        roleName: roleName,
        clan,
      });

      dispatch(actionsClanProfile.clearMembersOperation());
      dispatch(actionsClanProfile.updateMembers(currentClanId, membersUpdated));
    } catch (error: unknown) {
      const axiosError = error as AxiosCustomError<IApiChangeRoleError>;

      if (axiosError?.data) {
        sendNotificationByClanOperationalError(
          axiosError?.data,
          clan,
          membersToChange,
          sendChangeRoleErrorNotification,
        );

        return;
      }

      sendChangeRoleErrorNotification();
    }
  };

const createNewMember = (member: IClanMember, roleName: ROLE_NAMES) => {
  return { ...member, roleName };
};

export const changeCommander =
  (newCommander: number, isLeavingClan = false): AppAsyncThunk<boolean> =>
  async (dispatch, getState) => {
    const state = getState();
    const { clanId, id: currentAccountId } = state.currentAccount;

    if (!clanId) {
      return false;
    }

    const defaultRoleAfterCommander = ROLE_NAMES.EXECUTIVE_OFFICER;
    const newMembers = state.members.clans[clanId].members.map((member) => {
      if (member.id === newCommander) {
        return createNewMember(member, ROLE_NAMES.COMMANDER);
      } else if (member.id === currentAccountId) {
        return createNewMember(member, defaultRoleAfterCommander);
      }
      return member;
    });

    try {
      const url = urls.changeCommander.replace('{clan_id}', String(clanId));
      const payload = { user_id: newCommander };

      const response = await post<IApiChangeCommanderResponse>(url, payload);

      if ('error' in response) {
        throw response;
      }

      dispatch(actionsClanProfile.clearMembersOperation());
      dispatch(
        currentAccountActions.updateAccountInfo({
          roleName: defaultRoleAfterCommander,
        }),
      );
      dispatch(actionsClanProfile.updateMembers(clanId, newMembers));

      const state = getState();
      const currentClan = currentClanSelector(state);
      const member = newMembers.find((member) => member.id === newCommander);

      if (member && currentClan) {
        sendNewCommanderNotification(member, currentClan);
        sendCurrentAccountTransferredLeadershipNotification(defaultRoleAfterCommander, currentClan);
      }

      return true;
    } catch (error: unknown) {
      const axiosError = error as AxiosCustomError<IApiChangeCommanderError>;

      const reason = axiosError?.data.additional_info?.reason;

      if (reason === 'account_not_in_clan') {
        const state = getState();
        const currentClan = currentClanSelector(state);
        const member = newMembers.find((member) => member.id === newCommander);

        if (member && currentClan) {
          sendChangeCommanderErrorNotification(isLeavingClan, reason, member.name, currentClan);
        }

        return false;
      }

      sendChangeCommanderErrorNotification(isLeavingClan);

      return false;
    }
  };

const normalizeClan = (clan: ClanInfoTypeRaw): ClanInfoType => {
  const {
    accumulative_resource,
    community_urls,
    created_at,
    is_disbanded,
    max_members_count,
    personal_resource,
    pre_moderation,
    raw_description,
    ...others
  } = clan;

  return {
    accumulativeResource: accumulative_resource,
    communityUrls: community_urls,
    createdAt: created_at,
    isDisbanded: is_disbanded,
    maxMembersCount: max_members_count,
    personalResource: personal_resource,
    preModeration: pre_moderation,
    rawDescription: raw_description,
    ...others,
  };
};

export const fetchClan =
  (clanId: number, withGlobalSpinner = true): AppAsyncThunk =>
  async (dispatch, getState) => {
    dispatch(actionsClanProfile.startUpdateClan(clanId));

    const loader = withGlobalSpinner ? new Loader() : null;
    loader?.start(dispatch);

    try {
      const url = urls.clanInfo.replace('{clan_id}', `${clanId}`);

      const response = await get<IApiClanResponse>(url);

      if ('error' in response) {
        throw response.error;
      }

      const clanRaw = response.clanview.clan;
      if (!clanRaw || !clanRaw.id) {
        throw t('Произошла ошибка. Повторите попытку позже');
      }

      const clan = normalizeClan(clanRaw);
      const { clanRename } = getState();

      const wowsLadder = response.clanview.wows_ladder;
      const renamingInfo = response.clanview.renaming_info;

      let actions: Array<Actions> = [actionsClanProfile.updateClan(clan)];
      if (response.clanview.buildings) {
        actions = [...actions, actionsSupply.updateBase(clanId, response.clanview.buildings)];
        dispatch(updateSupplyDataThunk(response.clanview.buildings));
      }

      if (wowsLadder) {
        actions = [...actions, actionsClanProfile.updateWowsLadder(clanId, wowsLadder)];
      }

      if (response.clanview.achievements) {
        actions = [...actions, actionsClanProfile.updateAchievements(clanId, response.clanview.achievements)];
      }

      if (renamingInfo) {
        actions = [
          actionsClanRename.startRenamingClan(),
          actionsClanRename.setStatusUrlRenamingClan(renamingInfo.status_url),
          actionsClanRename.updateFieldValueRenamingClan('tag', renamingInfo.tag, null),
          actionsClanRename.updateFieldValueRenamingClan('name', renamingInfo.name, null),
          ...actions,
        ];
      } else if (clanRename.isProcessing) {
        actions = [actionsClanRename.stopRenamingClan(), ...actions];
      }

      return actions.forEach((action) => {
        dispatch(action);
      });
    } catch (error: unknown) {
      const message = typeof error === 'string' ? error : t('Произошла ошибка. Повторите попытку позже');
      dispatch(actionsClanProfile.setClanError(clanId, message));
      dispatch(actionsSupply.updateBase(clanId));
    } finally {
      loader?.stop(dispatch);
    }
  };

const normalizeMembers = (members: ClanMemberRaw[]): IClanMemberWithRole[] => {
  return members.map(
    (member) =>
      Object.fromEntries(
        Object.entries(member).map(([key, value]) => [snakeToCamel(key), value]),
      ) as IClanMemberWithRole,
  );
};

const applyCustomizedRole = (members: IClanMemberWithRole[]): IClanMember[] => {
  return members.map(({ role, ...rest }) => ({
    roleName: role.name,
    ...rest,
  }));
};

export const fetchMembers =
  (clanId: number, withGlobalSpinner = false, battleType: string | null = null): AppAsyncThunk =>
  async (dispatch, getState) => {
    const { members } = getState();

    const teamNumber = members.teamNumber[clanId];
    if (members.isFetching[clanId]) {
      return;
    }

    dispatch(actionsClanProfile.startUpdateMembers(clanId));

    let fetchMembersUrl = urls.fetchMembers
      .replace('{clan_id}', `${clanId}`)
      .replace('{battle_type}', members.battleTypes[clanId] || settings.defaultBattleType);

    if (battleType) {
      fetchMembersUrl = urls.fetchMembers.replace('{clan_id}', `${clanId}`).replace('{battle_type}', battleType);
    }

    const isCVCRegular =
      members.battleTypes[clanId] === BATTLE_TYPES.REGULAR_CVC || battleType === BATTLE_TYPES.REGULAR_CVC;
    const isCVCBrawl = members.battleTypes[clanId] === BATTLE_TYPES.BRAWL_CVC || battleType === BATTLE_TYPES.BRAWL_CVC;
    const isCVCAny = battleType === 'cvc_or_brawl';

    if (isCVCRegular || isCVCBrawl) {
      let season = members.currentSeason;
      const seasonType = isCVCRegular ? SEASON_TYPES.REGULAR : SEASON_TYPES.BRAWL;

      if (members.currentSeasonType !== seasonType) {
        dispatch(actionsClanProfile.setClanProfileCurrentSeasonType(seasonType));
        const lastSeason = getLastSeasonOfType(seasonType);
        if (lastSeason) {
          season = lastSeason.id;
          dispatch(actionsClanProfile.setClanProfileCurrentSeason(season));
        }
      }

      fetchMembersUrl =
        urls.fetchMembers.replace('{clan_id}', `${clanId}`).replace('{battle_type}', 'cvc') + '&season=' + season;

      if (teamNumber && isCVCRegular) {
        fetchMembersUrl = fetchMembersUrl + '&team_number=' + teamNumber;
      }
    }

    if (isCVCAny) {
      const season = members.currentSeason;
      fetchMembersUrl = urls.fetchMembers.replace('{clan_id}', `${clanId}`).replace('{battle_type}', 'cvc');
      fetchMembersUrl = season ? fetchMembersUrl + '&season=' + season : fetchMembersUrl;
    }

    const loader = withGlobalSpinner ? new Loader() : null;
    loader?.start(dispatch);

    try {
      const response = await get<IApiClanMembersResponse>(fetchMembersUrl);

      if (response.status !== 'ok') {
        dispatch(actionsClanProfile.setMembersError(clanId, response.error));
        return;
      }

      const isHiddenStatistics = response.is_hidden_statistics;
      const clanStatistics = response.clan_statistics;

      let members: IClanMember[] | IClanMemberWithRole[] = normalizeMembers(response.items);
      members = applyCustomizedRole(members);

      dispatch(actionsClanProfile.updateClanStatistics(clanId, isHiddenStatistics, clanStatistics));
      dispatch(actionsClanProfile.updateMembers(clanId, members));
    } catch {
      dispatch(actionsClanProfile.setMembersError(clanId, t('Произошла ошибка. Повторите попытку позже')));
      dispatch(actionsClanProfile.updateClanStatistics(clanId, false, null));
    } finally {
      loader?.stop(dispatch);
    }
  };
